import {
  ampathPerformerReference,
  ampathSystem,
  type CreateReference,
  createCodeableConcept,
  createExtensionValue,
  createIdentifier,
  ExtensionUrl,
  ServiceRequestCode,
  ServiceRequestIdentifier,
  valsOrNone,
} from '@quromedical/fhir-common';
import { createFormatter, type Fn, first, formatter } from '@quromedical/utils';
import { format } from 'date-fns';
import type { Extension } from 'fhir/r4';
import { z } from 'zod';
import { AdmissionGetResponseSchema } from '../admission/schema';
import type {
  AmpathPatient,
  CreateOrderRequest,
  Guarantor,
} from '../external-integrations';
import {
  MedicalAidMembershipSchema,
  MedicalAidPersonSchema,
} from '../medical-aid';
import { PartialPersonSchema, type PersonGeneral } from '../person';

import {
  type PathologyOrder,
  PathologyOrderCreateRequestBaseSchema,
} from './schema';

const RequiredAdmissionMedicalAidAuthorizationSchema = z.object({
  code: z.string(),
});

const RequiredPatientSchema = PartialPersonSchema;
type RequiredPatient = z.infer<typeof RequiredPatientSchema>;

const RequiredAdmissionSchema = AdmissionGetResponseSchema.pick({
  icd10Codes: true,
}).extend({
  medicalAidAuthorization:
    RequiredAdmissionMedicalAidAuthorizationSchema.optional(),
});
type RequiredAdmission = z.infer<typeof RequiredAdmissionSchema>;

const RequiredMedicalAidMainMemberSchema = MedicalAidPersonSchema.pick({
  contact: true,
  general: true,
}).partial();
type RequiredMedicalAidMainMember = z.infer<
  typeof RequiredMedicalAidMainMemberSchema
>;

const RequiredMedicalAidMembershipSchema = MedicalAidMembershipSchema.pick({
  dependantCode: true,
  medicalAid: true,
}).partial();
type RequiredMedicalAidMembership = z.infer<
  typeof RequiredMedicalAidMembershipSchema
>;

const toAmpathDate = (isoDateTime?: string) => {
  if (!isoDateTime) {
    return undefined;
  }

  const timestamp = new Date(isoDateTime).getTime();

  return format(timestamp, 'yyyy-MM-dd');
};

const toAmpathTime = (isoDateTime?: string) => {
  if (!isoDateTime) {
    return undefined;
  }

  const timestamp = new Date(isoDateTime).getTime();

  return format(timestamp, 'hh:mm:ss');
};

type AmpathGender = AmpathPatient['gender'];

/**
 * Defined using `Patient['gender'] & string` since `gender` can be `undefined` and that would not
 * be a valid map gender
 */
type FhirGender = fhir4.Patient['gender'] & string;

const genderMapping: Record<FhirGender, AmpathGender> = {
  female: 'F',
  male: 'M',
  other: 'U',
  unknown: 'U',
};

const getAmpathGender = (
  gender: fhir4.Patient['gender'] = 'unknown',
): AmpathGender => (gender && genderMapping[gender]) || 'U';

/**
 * Get the medical aid member name in the ampath format
 */
const getAmpathName = (general: Partial<PersonGeneral> = {}): string => {
  const { familyName = '', givenName = '' } = general;

  return `${familyName}, ${givenName}`;
};

const getPatient = (patient: RequiredPatient): AmpathPatient => {
  const gender = getAmpathGender(patient.general.gender);

  const name = getAmpathName(patient.general);

  // Ampath API breaks if the name is more than 30 characters
  const shortName = name.slice(0, 30);

  const phone = first(patient.contact?.telecomPhoneNumbers);
  const idNo = patient.general.identifierValue;

  return {
    gender,
    name: shortName,
    homeTelNo: phone,
    idNo,
  };
};

interface GetGuarantorProps {
  patient: RequiredPatient;
  medicalAidMembership: RequiredMedicalAidMembership;
  medicalAidMainMember: RequiredMedicalAidMainMember;
  admission: RequiredAdmission;
}

const getGuarantor: Fn<GetGuarantorProps, Guarantor> = ({
  patient,
  admission,
  medicalAidMainMember,
  medicalAidMembership,
}) => {
  // fallback to the patient information even if they are not the main member if we don't have a
  // related person provided
  const name = getAmpathName(medicalAidMainMember.general);

  // validation schema on create should enforce these are defined
  const medicalAidName = medicalAidMembership.medicalAid?.schemeName;
  const medicalAidNo = medicalAidMembership?.medicalAid?.membershipNumber;

  const medicalAidDependantCode = parseInt(
    medicalAidMembership.dependantCode || '0',
    10,
  );

  if (!(medicalAidName && medicalAidNo)) {
    throw new Error('Invalid medical aid name or number');
  }

  const { general: mainMemberGeneral = {}, contact: mainMemberContact = {} } =
    medicalAidMainMember;

  const patientContact = patient.contact;

  // as discussed with ampath, to give patient address if we don't have one for the main member
  const addressline1 =
    first(mainMemberContact.addressLines) || first(patientContact.addressLines);
  const addresscity =
    mainMemberContact.addressCity || patientContact.addressCity;

  if (!(addresscity && addressline1)) {
    throw new Error('Guarantor address and city are required');
  }

  // some medical aids don't give use the main member ID
  const idNo = mainMemberGeneral.identifierValue || 'NA';

  const phoneNumber = first(mainMemberContact.telecomPhoneNumbers) || 'NA';
  const homeTelNo = phoneNumber;
  const cellphoneNo = phoneNumber;

  const medicalAidAuthorizationNo = admission.medicalAidAuthorization?.code;

  // Ampath needs a short form of the medical aid name ("CODE") max 10 chars
  const shortMedicalAidName = medicalAidName.slice(0, 10);

  // Ampath API breaks if the address is more than approximately 40 characters
  const shortAddressCity = addresscity.slice(0, 40);
  // TODO: We need to keep note of this @Vernon-van-der-Merwe
  // These ampath checks,mappings and models need to only be used in the ampath client
  // Along with all the other ampath specific checks, and not leak into the rest of the project

  // Ampath API breaks if the name is more than 30 characters
  const shortName = name.slice(0, 30);

  return {
    addresscity: shortAddressCity,
    addressline1,
    homeTelNo,
    cellphoneNo,
    name: shortName,
    idNo,
    medicalAidName: shortMedicalAidName,
    medicalAidNo,
    medicalAidDependantCode,
    medicalAidAuthorizationNo,
  };
};

export const CreateAmpathOrderModelPropsSchema = z.object({
  doctorAlias: z.string(),
  createRequest: PathologyOrderCreateRequestBaseSchema,
  patient: RequiredPatientSchema,
  admission: RequiredAdmissionSchema,
  medicalAidMembership: RequiredMedicalAidMembershipSchema,
  medicalAidMainMember: RequiredMedicalAidMainMemberSchema,
});
export type CreateAmpathOrderModelProps = z.infer<
  typeof CreateAmpathOrderModelPropsSchema
>;

const mapOrderToAmpathModel: Fn<
  CreateAmpathOrderModelProps,
  CreateOrderRequest
> = ({
  createRequest,
  doctorAlias: doctor,
  patient,
  medicalAidMembership,
  medicalAidMainMember,
  admission,
}) => {
  const {
    comment,
    testCodes: orderedTests,
    sampleDateTime,
    copyDoctors = [],
  } = createRequest;
  const ampathPatient = getPatient(patient);

  const guarantor = getGuarantor({
    patient,
    medicalAidMembership,
    medicalAidMainMember,
    admission,
  });

  const collectionDate = toAmpathDate(sampleDateTime);
  const collectionTime = toAmpathTime(sampleDateTime);

  const admissionIcd10Codes = admission.icd10Codes?.map((code) => code.code);
  const admissionIcd10Code = admissionIcd10Codes?.join(',');

  // Ampath expect a hospital name to ensure patients get billed from their in hospital benefit
  const hospital = {
    name: 'Quro Medical Sent In',
  };

  return {
    doctor,
    priority: 'S',
    collectionDate,
    collectionTime,
    orderedTests,
    comment,
    patient: ampathPatient,
    guarantor,
    admissionIcd10Code,
    copyDoctors,

    // these will always be empty for our case
    admissionQuerries: [],
    orderQuerries: [],
    hospital,
  };
};

interface CreateAmpathServiceRequestFromOrderProps {
  form: PathologyOrder;
  orderNumber: string;
  priority?: fhir4.ServiceRequest['priority'];
  status?: fhir4.ServiceRequest['status'];
}

const createCopyDoctorExtension = createExtensionValue(
  ExtensionUrl.serviceRequestCopyPractitioners,
);

const createCopyDoctorsExtension = (
  copyDoctor?: CreateReference,
): Extension | undefined => {
  if (!copyDoctor) {
    return undefined;
  }
  const extension = createCopyDoctorExtension(copyDoctor);
  return extension;
};

/**
 * Creates an ampath order in the draft status, the order will be fulfilled by ampath and updated by
 * way of the relevant order jobs as needed
 */
export const createAmpathServiceRequestFromOrder: Fn<
  CreateAmpathServiceRequestFromOrderProps,
  Omit<fhir4.ServiceRequest, 'id'>
> = ({ form, orderNumber, priority = 'stat', status = 'draft' }) => {
  const {
    sampleDateTime: occurrenceDateTime,
    testCodes,
    comment,
    patient: subject,
    requestor: requester,
    copyDoctors = [],
  } = form;

  const orderDetail = testCodes.map((test) =>
    createCodeableConcept(test.code, test.display, ampathSystem),
  );

  const code = createCodeableConcept(ServiceRequestCode.PathologyRequest);

  const identifier: fhir4.Identifier[] = [
    createIdentifier(ServiceRequestIdentifier.AmpathOrderNumber, orderNumber),
  ];

  const note: fhir4.Annotation[] | undefined = comment
    ? [
        {
          text: comment,
        },
      ]
    : undefined;

  const extension = valsOrNone(copyDoctors.map(createCopyDoctorsExtension));

  return {
    resourceType: 'ServiceRequest',
    intent: 'order',
    status,
    priority,
    orderDetail,
    occurrenceDateTime,
    subject,
    code,
    note,
    requester,
    identifier,
    performer: [ampathPerformerReference],
    extension,
  };
};

const formatAmpathPayload = createFormatter<CreateOrderRequest>({
  guarantor: {
    idNo: formatter.length(15),
    homeTelNo: formatter.length(18),
    cellphoneNo: formatter.length(18),
    addresscity: formatter.length(40),
    addressline1: formatter.length(30),
    medicalAidNo: formatter.length(15),
    medicalAidName: formatter.length(15),
    email: formatter.optional(formatter.length(40)),
    medicalAidDependantCode: formatter.optional(formatter.round),
    medicalAidAuthorizationNo: formatter.optional(formatter.length(15)),
  },
  patient: {
    name: formatter.length(30),
    idNo: formatter.optional(formatter.length(13)),
    title: formatter.optional(formatter.length(10)),
    homeTelNo: formatter.optional(formatter.length(18)),
    workTelNo: formatter.optional(formatter.length(18)),
    reference: formatter.optional(formatter.length(20)),
    dob: formatter.optional(toAmpathDate),
  },

  admissionIcd10Code: formatter.optional(formatter.length(7)),
  orderedTests: formatter.optional(formatter.array(formatter.length(13))),

  //ampath internal use
  admissionQuerries: formatter.constant([]),
  orderQuerries: formatter.constant([]),
  copyDoctors: formatter.array(formatter.length(10)),
  hospital: {
    name: formatter.optional(formatter.trim),
  },
});

export const createAmpathOrderModel: Fn<
  CreateAmpathOrderModelProps,
  CreateOrderRequest
> = (order) => {
  const request = mapOrderToAmpathModel(order);

  return formatAmpathPayload(request);
};
